import React, { useEffect, useState } from "react";
import { ToastContainer, toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { Container, Row, Col, Card, Form, Button, Table, Badge, Alert } from "react-bootstrap";
import { FaPlus, FaEdit, FaTrash, FaArrowLeft, FaSearch, FaBed, FaCheck, FaTimes, FaHotel, FaBan, FaExclamationTriangle, FaSync } from "react-icons/fa";
import { useCamas } from "../../../services/hooks/useCamas";
import { useHabitaciones } from "../../../services/hooks/useHabitaciones";
import { useModulos } from "../../../services/hooks/useModulos";
import { useZonas } from "../../../services/hooks/useZonas";
import camaApi from "../../../services/api/camaApi";

const esCamaActiva = (cama) =>
  cama?.activo === true || cama?.activo === 1 || cama?.activo == null;

const MantenimientoCamas = () => {
  const navigate = useNavigate();

  // Hooks para manejo de datos
  const {
    camas,
    loading,
    error,
    fetchCamasWithStats,
    createCama,
    updateCama,
    deleteCama,
    clearError
  } = useCamas();

  const { habitaciones, fetchHabitacionesByModulo } = useHabitaciones();
  const { modulos, fetchModulos } = useModulos();
  const { zonas, fetchZonas } = useZonas();

  const [state, setState] = useState({
    zonaSeleccionada: null,
    moduloSeleccionado: null,
    habitacionFiltro: null,
    camaSeleccionada: null,
    filtro: "",
  });

  const [showEstadoModal, setShowEstadoModal] = useState(false);
  const [camaEstadoTarget, setCamaEstadoTarget] = useState(null);
  const [validacionInactivacion, setValidacionInactivacion] = useState(null);
  const [loadingValidacionEstado, setLoadingValidacionEstado] = useState(false);
  const [procesandoEstado, setProcesandoEstado] = useState(false);
  const [errorModalEstado, setErrorModalEstado] = useState(null);

  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [camaToEdit, setCamaToEdit] = useState(null);
  const [formData, setFormData] = useState({
    codigo: '',
    habitacion_id: null,
    estado_cama_id: 1,
    id_perfil: null
  });
  const [perfiles, setPerfiles] = useState([]);
  const [sincronizandoCamaId, setSincronizandoCamaId] = useState(null);

  // Cargar zonas al montar el componente
  useEffect(() => {
    const selectedProject = JSON.parse(localStorage.getItem('selectedProject'));
    if (selectedProject && selectedProject.id) {
      fetchZonas(selectedProject.id);
    }
    camaApi.getPerfiles()
      .then((res) => {
        const data = res.data;
        setPerfiles(Array.isArray(data) ? data : data?.data || []);
      })
      .catch(() => setPerfiles([]));
  }, [fetchZonas]);

  // Filtrar camas según búsqueda por código
  const camasFiltradas = camas.filter((cama) => {
    const codigo = (cama.codigo || cama.numero || "").toString();
    return codigo.toLowerCase().includes(state.filtro.toLowerCase());
  });

  const cargarCamasModulo = async (moduloId, habitacionId = null) => {
    await fetchCamasWithStats(habitacionId, null, moduloId);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setFormData({
      codigo: '',
      habitacion_id: null,
      estado_cama_id: 1,
      id_perfil: null
    });
    setCamaToEdit(null);
  };

  const handleOpenCreateModal = () => {
    setModalMode('create');
    setFormData({
      codigo: '',
      habitacion_id: state.habitacionFiltro?.id || habitaciones[0]?.id || null,
      estado_cama_id: 1,
      id_perfil: null
    });
    setCamaToEdit(null);
    setShowModal(true);
  };

  const handleOpenEditModal = (cama) => {
    setModalMode('edit');
    setFormData({
      codigo: cama.codigo || String(cama.numero || ''),
      habitacion_id: cama.habitacion_id,
      estado_cama_id: cama.estado_cama_id,
      id_perfil: cama.id_perfil || null
    });
    setCamaToEdit(cama);
    setShowModal(true);
  };

  const handleSincronizarLogiflex = async (cama) => {
    const selectedProject = JSON.parse(localStorage.getItem("selectedProject") || "null");
    if (!selectedProject?.id) {
      toast.error("Seleccione un proyecto");
      return;
    }
    setSincronizandoCamaId(cama.id);
    try {
      const response = await camaApi.sincronizarLogiflex(selectedProject.id, cama.id);
      const data = response.data;
      if (data?.success) {
        toast.success(`Homologada en Logiflex (${data.accion})`);
        if (state.moduloSeleccionado) {
          await cargarCamasModulo(
            state.moduloSeleccionado.id,
            state.habitacionFiltro?.id || null
          );
        }
      } else {
        toast.error(data?.motivo || "No se pudo homologar");
      }
    } catch (error) {
      toast.error(
        error.response?.data?.detail || error.message || "Error al homologar con Logiflex"
      );
    } finally {
      setSincronizandoCamaId(null);
    }
  };

  const handleSaveCama = async () => {
    try {
      if (modalMode === 'create') {
        await createCama(formData);
      } else {
        await updateCama(camaToEdit.id, formData);
      }
      if (state.moduloSeleccionado) {
        await cargarCamasModulo(
          state.moduloSeleccionado.id,
          state.habitacionFiltro?.id || null
        );
      }
      handleCloseModal();
    } catch (error) {
      console.error('Error al guardar cama:', error);
    }
  };

  const handleDeleteCama = async (cama) => {
    const codigo = cama.codigo || cama.numero;
    if (window.confirm(`¿Estás seguro de eliminar la cama ${codigo}?`)) {
      try {
        await deleteCama(cama.id);
        if (state.moduloSeleccionado) {
          await cargarCamasModulo(
            state.moduloSeleccionado.id,
            state.habitacionFiltro?.id || null
          );
        }
        setState(prev => ({ ...prev, camaSeleccionada: null }));
      } catch (error) {
        console.error('Error al eliminar cama:', error);
      }
    }
  };

  const handleZonaChange = async (zonaId) => {
    if (zonaId) {
      const zona = zonas.find(z => z.id === zonaId);
      setState({ ...state, zonaSeleccionada: zona, moduloSeleccionado: null, habitacionFiltro: null, camaSeleccionada: null });
      await fetchModulos(zonaId);
    } else {
      setState({ ...state, zonaSeleccionada: null, moduloSeleccionado: null, habitacionFiltro: null, camaSeleccionada: null });
    }
  };

  const handleModuloChange = async (moduloId) => {
    if (moduloId) {
      const modulo = modulos.find(m => m.id === moduloId);
      setState({ ...state, moduloSeleccionado: modulo, habitacionFiltro: null, camaSeleccionada: null });
      await fetchHabitacionesByModulo(moduloId);
      await cargarCamasModulo(moduloId);
    } else {
      setState({ ...state, moduloSeleccionado: null, habitacionFiltro: null, camaSeleccionada: null });
    }
  };

  const handleHabitacionFiltroChange = async (habitacionId) => {
    if (!state.moduloSeleccionado) return;
    if (habitacionId) {
      const habitacion = habitaciones.find(h => h.id === habitacionId);
      setState({ ...state, habitacionFiltro: habitacion, camaSeleccionada: null });
      await cargarCamasModulo(state.moduloSeleccionado.id, habitacionId);
    } else {
      setState({ ...state, habitacionFiltro: null, camaSeleccionada: null });
      await cargarCamasModulo(state.moduloSeleccionado.id);
    }
  };

  const handleCerrarModalEstado = () => {
    setShowEstadoModal(false);
    setCamaEstadoTarget(null);
    setValidacionInactivacion(null);
    setLoadingValidacionEstado(false);
    setProcesandoEstado(false);
    setErrorModalEstado(null);
  };

  const handleAbrirModalEstado = async (cama) => {
    setCamaEstadoTarget(cama);
    setValidacionInactivacion(null);
    setErrorModalEstado(null);
    setShowEstadoModal(true);

    if (!esCamaActiva(cama)) return;

    setLoadingValidacionEstado(true);
    try {
      const response = await camaApi.validarInactivacionCama(cama.id);
      setValidacionInactivacion(response.data);
    } catch (error) {
      setErrorModalEstado(
        error.response?.data?.detail || "No se pudo validar la cama. Intente nuevamente."
      );
    } finally {
      setLoadingValidacionEstado(false);
    }
  };

  const handleConfirmarCambioEstado = async () => {
    if (!camaEstadoTarget) return;
    const activa = esCamaActiva(camaEstadoTarget);
    if (activa && validacionInactivacion && !validacionInactivacion.puede_inactivar) return;

    setProcesandoEstado(true);
    setErrorModalEstado(null);
    try {
      const response = await camaApi.cambiarEstadoCama(camaEstadoTarget.id, !activa);
      const actualizada = response.data;
      setState(prev => ({
        ...prev,
        camaSeleccionada: actualizada,
      }));
      if (state.moduloSeleccionado) {
        await cargarCamasModulo(
          state.moduloSeleccionado.id,
          state.habitacionFiltro?.id || null
        );
      }
      toast.success(activa ? "Cama inactivada exitosamente" : "Cama activada exitosamente");
      handleCerrarModalEstado();
    } catch (error) {
      const detalle = error.response?.data?.detail;
      setErrorModalEstado(
        typeof detalle === "string"
          ? detalle
          : "No se pudo cambiar el estado de la cama."
      );
    } finally {
      setProcesandoEstado(false);
    }
  };

  const esInactivacion = camaEstadoTarget && esCamaActiva(camaEstadoTarget);
  const puedeConfirmarEstado =
    !procesandoEstado &&
    !loadingValidacionEstado &&
    (!esInactivacion || validacionInactivacion?.puede_inactivar);

  const getEstadoBadge = (estado) => {
    const estados = {
      1: { text: 'Libre', variant: 'success' },
      2: { text: 'Ocupada', variant: 'danger' },
      3: { text: 'Averiada', variant: 'warning' }
    };
    const estadoInfo = estados[estado] || { text: 'Desconocido', variant: 'secondary' };
    return <Badge bg={estadoInfo.variant}>{estadoInfo.text}</Badge>;
  };

  return (
    <>
      {/* Digital Harmony Styles para Mantenimiento Camas */}
      <style>
        {`
          /* Fondo degradado */
          .dh-maintenance-bg {
            background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
            min-height: 100vh;
          }
          
          /* Header principal */
          .dh-main-header {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            padding: 2rem 0;
            margin-bottom: 2rem;
            border-radius: 0 0 30px 30px;
            box-shadow: 0 10px 30px rgba(102, 126, 234, 0.2);
          }
          
          .dh-main-title {
            font-size: 2.2rem;
            font-weight: 700;
            margin: 0;
          }
          
          .dh-main-subtitle {
            font-size: 1.1rem;
            opacity: 0.9;
            margin: 0.5rem 0 0 0;
          }
          
          /* Cards con Digital Harmony */
          .dh-card {
            background: white;
            border-radius: 20px;
            box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
            border: none;
            overflow: hidden;
            transition: all 0.3s ease;
          }
          
          .dh-card:hover {
            transform: translateY(-5px);
            box-shadow: 0 15px 40px rgba(0, 0, 0, 0.15);
          }
          
          .dh-card-header {
            background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
            border-bottom: 2px solid #dee2e6;
            padding: 1.5rem;
            font-weight: 600;
            font-size: 1.2rem;
            color: #495057;
          }
          
          .dh-card-body {
            padding: 2rem;
          }
          
          /* Botones Digital Harmony */
          .btn-dh-primary {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            border: none;
            border-radius: 12px;
            padding: 0.75rem 1.5rem;
            font-weight: 600;
            color: white;
            transition: all 0.3s ease;
            box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);
          }
          
          .btn-dh-primary:hover {
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(102, 126, 234, 0.4);
            color: white;
          }
          
          .btn-dh-secondary {
            background: linear-gradient(135deg, #6c757d 0%, #495057 100%);
            border: none;
            border-radius: 12px;
            padding: 0.75rem 1.5rem;
            font-weight: 600;
            color: white;
            transition: all 0.3s ease;
            box-shadow: 0 4px 15px rgba(108, 117, 125, 0.3);
          }
          
          .btn-dh-secondary:hover {
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(108, 117, 125, 0.4);
            color: white;
          }
          
          .btn-dh-success {
            background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
            border: none;
            border-radius: 12px;
            padding: 0.5rem 1rem;
            font-weight: 600;
            color: white;
            transition: all 0.3s ease;
            box-shadow: 0 4px 15px rgba(40, 167, 69, 0.3);
          }
          
          .btn-dh-success:hover {
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(40, 167, 69, 0.4);
            color: white;
          }
          
          .btn-dh-danger {
            background: linear-gradient(135deg, #dc3545 0%, #e83e8c 100%);
            border: none;
            border-radius: 12px;
            padding: 0.5rem 1rem;
            font-weight: 600;
            color: white;
            transition: all 0.3s ease;
            box-shadow: 0 4px 15px rgba(220, 53, 69, 0.3);
          }
          
          .btn-dh-danger:hover {
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(220, 53, 69, 0.4);
            color: white;
          }
          
          /* Formularios Digital Harmony */
          .dh-form-control {
            border: 2px solid #e9ecef;
            border-radius: 12px;
            padding: 0.75rem 1rem;
            font-size: 1rem;
            transition: all 0.3s ease;
            background: #f8f9fa;
          }
          
          .dh-form-control:focus {
            border-color: #667eea;
            box-shadow: 0 0 0 0.2rem rgba(102, 126, 234, 0.25);
            background: white;
          }
          
          /* Tabla Digital Harmony */
          .dh-table {
            background: white;
            border-radius: 15px;
            overflow: hidden;
            box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
          }
          
          .dh-table thead th {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            border: none;
            padding: 1rem;
            font-weight: 600;
            font-size: 0.95rem;
          }
          
          .dh-table tbody td {
            padding: 1rem;
            border-bottom: 1px solid #f1f3f4;
            vertical-align: middle;
          }
          
          .dh-table tbody tr:hover {
            background: #f8f9fa;
          }
          
          /* Modal Digital Harmony */
          .dh-modal .modal-content {
            border-radius: 20px;
            border: none;
            box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
          }
          
          .dh-modal .modal-header {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            border-radius: 20px 20px 0 0;
            border: none;
            padding: 1.5rem;
          }
          
          .dh-modal .modal-body {
            padding: 2rem;
          }
          
          .dh-modal .modal-footer {
            border: none;
            padding: 1.5rem 2rem;
            background: #f8f9fa;
            border-radius: 0 0 20px 20px;
          }
          
          /* Estados y badges */
          .dh-badge {
            padding: 0.5rem 1rem;
            border-radius: 20px;
            font-weight: 600;
            font-size: 0.85rem;
          }
          
          /* Animaciones */
          @keyframes fadeInUp {
            from {
              opacity: 0;
              transform: translateY(30px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          
          .dh-fade-in {
            animation: fadeInUp 0.6s ease-out;
          }
          
          /* Responsive */
          @media (max-width: 768px) {
            .dh-main-title {
              font-size: 1.8rem;
            }
            
            .dh-card-body {
              padding: 1.5rem;
            }
          }

          .dh-modal-estado .modal-content {
            border: none;
            border-radius: 20px;
            overflow: hidden;
            box-shadow: 0 20px 50px rgba(0,0,0,0.2);
          }

          .dh-modal-estado .modal-header {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            border: none;
            padding: 1.25rem 1.5rem;
          }

          .dh-modal-estado .modal-header .btn-close {
            filter: brightness(0) invert(1);
          }

          .dh-alert {
            border: none;
            border-radius: 12px;
            padding: 1rem 1.25rem;
          }

          .dh-alert-info {
            background: linear-gradient(135deg, rgba(102, 126, 234, 0.12) 0%, rgba(118, 75, 162, 0.08) 100%);
            color: #374151;
            border-left: 4px solid #667eea;
          }

          .dh-alert-warning {
            background: linear-gradient(135deg, rgba(251, 191, 36, 0.15) 0%, rgba(245, 158, 11, 0.1) 100%);
            color: #92400e;
            border-left: 4px solid #f59e0b;
          }

          .dh-alert-danger {
            background: linear-gradient(135deg, rgba(239, 68, 68, 0.12) 0%, rgba(220, 38, 38, 0.08) 100%);
            color: #991b1b;
            border-left: 4px solid #ef4444;
          }
        `}
      </style>

      <div className="dh-maintenance-bg">
        {/* Header Principal */}
        <div className="dh-main-header">
          <Container>
            <Row className="align-items-center">
              <Col md={8}>
                <div className="d-flex align-items-center">
                  <Button
                    variant="outline-light"
                    className="me-3 btn-dh-secondary"
                    onClick={() => navigate('/mantenimientos')}
                  >
                    <FaArrowLeft className="me-2" />
                    Volver
                  </Button>
                  <div>
                    <h1 className="dh-main-title">
                      <FaBed className="me-3" />
                      Mantenimiento de Camas
                    </h1>
                    <p className="dh-main-subtitle">
                      Gestiona las camas de las habitaciones hoteleras
                    </p>
                  </div>
                </div>
              </Col>
              <Col md={4} className="text-end">
                <Button
                  className="btn-dh-primary"
                  onClick={handleOpenCreateModal}
                  disabled={!state.moduloSeleccionado || habitaciones.length === 0}
                >
                  <FaPlus className="me-2" />
                  Nueva Cama
                </Button>
              </Col>
            </Row>
          </Container>
        </div>

        <Container className="dh-fade-in">
          {/* Filtros y Selección */}
          <Row className="mb-4">
            <Col md={12}>
              <Card className="dh-card">
                <Card.Header className="dh-card-header">
                  <FaSearch className="me-2" />
                  Filtros y Selección
                </Card.Header>
                <Card.Body className="dh-card-body">
                  <Row>
                    <Col md={3}>
                      <Form.Group className="mb-3">
                        <Form.Label className="fw-bold">
                          <FaHotel className="me-2" />
                          Seleccionar Zona *
                        </Form.Label>
                        <Form.Select
                          className="dh-form-control"
                          value={state.zonaSeleccionada?.id || ''}
                          onChange={(e) => handleZonaChange(parseInt(e.target.value) || null)}
                        >
                          <option value="">Seleccione una zona...</option>
                          {zonas.map((zona) => (
                            <option key={zona.id} value={zona.id}>
                              {zona.nombre}
                            </option>
                          ))}
                        </Form.Select>
                        <Form.Text className="text-muted">
                          Seleccione una zona para ver sus módulos
                        </Form.Text>
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group className="mb-3">
                        <Form.Label className="fw-bold">
                          <FaHotel className="me-2" />
                          Seleccionar Módulo *
                        </Form.Label>
                        <Form.Select
                          className="dh-form-control"
                          value={state.moduloSeleccionado?.id || ''}
                          onChange={(e) => handleModuloChange(parseInt(e.target.value) || null)}
                          disabled={!state.zonaSeleccionada}
                        >
                          <option value="">Seleccione un módulo...</option>
                          {modulos
                            .filter(modulo => modulo.zona_id === state.zonaSeleccionada?.id)
                            .map((modulo) => (
                            <option key={modulo.id} value={modulo.id}>
                              {modulo.nombre} - {modulo.codigo}
                            </option>
                          ))}
                        </Form.Select>
                        <Form.Text className="text-muted">
                          Seleccione un módulo para listar todas sus camas
                        </Form.Text>
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group className="mb-3">
                        <Form.Label className="fw-bold">
                          <FaBed className="me-2" />
                          Filtrar por habitación
                        </Form.Label>
                        <Form.Select
                          className="dh-form-control"
                          value={state.habitacionFiltro?.id || ''}
                          onChange={(e) => handleHabitacionFiltroChange(parseInt(e.target.value) || null)}
                          disabled={!state.moduloSeleccionado}
                        >
                          <option value="">Todas las habitaciones</option>
                          {habitaciones.map((habitacion) => (
                            <option key={habitacion.id} value={habitacion.id}>
                              {habitacion.numero} - Piso {habitacion.piso}
                            </option>
                          ))}
                        </Form.Select>
                        <Form.Text className="text-muted">
                          Filtro opcional sobre las camas del módulo
                        </Form.Text>
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group className="mb-3">
                        <Form.Label className="fw-bold">
                          <FaSearch className="me-2" />
                          Buscar Cama
                        </Form.Label>
                        <Form.Control
                          type="text"
                          className="dh-form-control"
                          placeholder="Buscar por número..."
                          value={state.filtro}
                          onChange={(e) => setState({ ...state, filtro: e.target.value })}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Tabla de Camas */}
          <Row>
            <Col md={12}>
              <Card className="dh-card">
                <Card.Header className="dh-card-header">
                  <FaBed className="me-2" />
                  Camas
                  {state.moduloSeleccionado && (
                    <span className="ms-2 text-muted">
                      - Módulo {state.moduloSeleccionado.nombre}
                      {state.habitacionFiltro ? ` / Hab. ${state.habitacionFiltro.numero}` : ''}
                    </span>
                  )}
                </Card.Header>
                <Card.Body className="dh-card-body p-0">
                  {error && (
                    <Alert variant="danger" className="m-3">
                      {error}
                      <Button variant="outline-danger" size="sm" className="ms-2" onClick={clearError}>
                        Cerrar
                      </Button>
                    </Alert>
                  )}

                  {state.moduloSeleccionado && (
                    <div className="px-3 pt-3 d-flex gap-2 flex-wrap">
                      <Button
                        className="btn-dh-secondary btn-sm"
                        disabled={!state.camaSeleccionada}
                        onClick={() => state.camaSeleccionada && handleAbrirModalEstado(state.camaSeleccionada)}
                      >
                        {state.camaSeleccionada && !esCamaActiva(state.camaSeleccionada) ? (
                          <><FaCheck className="me-1" /> Activar cama</>
                        ) : (
                          <><FaBan className="me-1" /> Inactivar cama</>
                        )}
                      </Button>
                    </div>
                  )}

                  {loading ? (
                    <div className="text-center p-4">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Cargando...</span>
                      </div>
                      <p className="mt-2 text-muted">Cargando camas...</p>
                    </div>
                  ) : !state.moduloSeleccionado ? (
                    <div className="text-center p-5">
                      <FaBed size={48} className="text-muted mb-3" />
                      <h5 className="text-muted">Seleccione zona y módulo</h5>
                      <p className="text-muted">Al elegir un módulo se listan todas sus camas. La habitación es un filtro opcional.</p>
                    </div>
                  ) : camasFiltradas.length === 0 ? (
                    <div className="text-center p-5">
                      <FaBed size={48} className="text-muted mb-3" />
                      <h5 className="text-muted">No hay camas</h5>
                      <p className="text-muted">
                        {state.filtro
                          ? 'No se encontraron camas con el filtro aplicado'
                          : 'Este módulo no tiene camas registradas'
                        }
                      </p>
                      {!state.filtro && (
                        <Button className="btn-dh-primary" onClick={handleOpenCreateModal}>
                          <FaPlus className="me-2" />
                          Crear Primera Cama
                        </Button>
                      )}
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <Table className="dh-table mb-0">
                        <thead>
                          <tr>
                            <th style={{ width: "40px" }}></th>
                            <th>Código</th>
                            <th>Habitación</th>
                            <th>Perfil</th>
                            <th>Estado cama</th>
                            <th>Activa</th>
                            <th>Logiflex</th>
                            <th>Creada</th>
                            <th width="190">Acciones</th>
                          </tr>
                        </thead>
                        <tbody>
                          {camasFiltradas.map((cama) => (
                            <tr
                              key={cama.id}
                              className={state.camaSeleccionada?.id === cama.id ? 'table-primary' : ''}
                              style={{ cursor: 'pointer' }}
                              onClick={() => setState({ ...state, camaSeleccionada: cama })}
                            >
                              <td>
                                <Form.Check
                                  type="radio"
                                  checked={state.camaSeleccionada?.id === cama.id}
                                  onChange={() => setState({ ...state, camaSeleccionada: cama })}
                                />
                              </td>
                              <td>
                                <strong>{cama.codigo || cama.numero}</strong>
                              </td>
                              <td>
                                <Badge bg="info" className="dh-badge">
                                  {cama.habitacion_numero || cama.numero_habitacion || 'N/A'}
                                </Badge>
                              </td>
                              <td>
                                {cama.perfil_nombre || cama.perfil_codigo ? (
                                  <Badge bg="secondary">{cama.perfil_nombre || cama.perfil_codigo}</Badge>
                                ) : (
                                  <span className="text-muted">—</span>
                                )}
                              </td>
                              <td>
                                {getEstadoBadge(cama.estado_cama_id)}
                              </td>
                              <td>
                                {esCamaActiva(cama) ? (
                                  <Badge bg="success">Activa</Badge>
                                ) : (
                                  <Badge bg="secondary">Inactiva</Badge>
                                )}
                              </td>
                              <td>
                                {cama.id_proveedor ? (
                                  <small title={cama.codigo_proveedor || ""}>
                                    {String(cama.id_proveedor).slice(0, 8)}…
                                  </small>
                                ) : (
                                  <span className="text-muted">Pendiente</span>
                                )}
                              </td>
                              <td>
                                <span className="text-muted">
                                  {cama.created_at ? new Date(cama.created_at).toLocaleDateString() : '—'}
                                </span>
                              </td>
                              <td onClick={(e) => e.stopPropagation()}>
                                <div className="d-flex gap-2">
                                  <Button
                                    variant="outline-primary"
                                    size="sm"
                                    className="btn-dh-success"
                                    onClick={() => handleOpenEditModal(cama)}
                                    title="Editar cama"
                                  >
                                    <FaEdit />
                                  </Button>
                                  <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    disabled={sincronizandoCamaId === cama.id}
                                    onClick={() => handleSincronizarLogiflex(cama)}
                                    title="Homologar con Logiflex (codigo = ID SGC)"
                                  >
                                    <FaSync className={sincronizandoCamaId === cama.id ? "fa-spin" : ""} />
                                  </Button>
                                  <Button
                                    variant="outline-danger"
                                    size="sm"
                                    className="btn-dh-danger"
                                    onClick={() => handleDeleteCama(cama)}
                                    title="Eliminar cama"
                                  >
                                    <FaTrash />
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>

        {/* Modal para Crear/Editar Cama */}
        {showModal && (
          <div className="modal show d-block dh-modal" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    <FaBed className="me-2" />
                    {modalMode === 'create' ? 'Nueva Cama' : 'Editar Cama'}
                  </h5>
                  <button type="button" className="btn-close btn-close-white" onClick={handleCloseModal}></button>
                </div>
                <div className="modal-body">
                  <Form onSubmit={(e) => { e.preventDefault(); handleSaveCama(); }}>
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label className="fw-bold">
                            <FaBed className="me-2" />
                            Código de cama *
                          </Form.Label>
                          <Form.Control
                            type="text"
                            className="dh-form-control"
                            placeholder="Ej: A, B, 1"
                            value={formData.codigo || ''}
                            onChange={(e) => setFormData({...formData, codigo: e.target.value})}
                            required
                            maxLength={20}
                          />
                          <Form.Text className="text-muted">
                            Número de la cama dentro de la habitación (1-20)
                          </Form.Text>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label className="fw-bold">
                            <FaBed className="me-2" />
                            Estado
                          </Form.Label>
                          <Form.Select
                            className="dh-form-control"
                            value={formData.estado_cama_id || 1}
                            onChange={(e) => setFormData({...formData, estado_cama_id: parseInt(e.target.value)})}
                          >
                            <option value={1}>Libre</option>
                            <option value={2}>Ocupada</option>
                            <option value={3}>Averiada</option>
                          </Form.Select>
                          <Form.Text className="text-muted">
                            Estado actual de la cama
                          </Form.Text>
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row>
                      <Col md={12}>
                        <Form.Group className="mb-3">
                          <Form.Label className="fw-bold">Perfil Logiflex</Form.Label>
                          <Form.Select
                            className="dh-form-control"
                            value={formData.id_perfil || ""}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                id_perfil: e.target.value ? parseInt(e.target.value, 10) : null,
                              })
                            }
                          >
                            <option value="">Sin perfil</option>
                            {perfiles.map((p) => (
                              <option key={p.id} value={p.id}>
                                {p.nombre} ({p.codigo})
                              </option>
                            ))}
                          </Form.Select>
                          <Form.Text className="text-muted">
                            Valor enviado a Logiflex: WORKER, STAFF o CONTRATISTA
                          </Form.Text>
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row>
                      <Col md={12}>
                        <Form.Group className="mb-3">
                          <Form.Label className="fw-bold">
                            <FaBed className="me-2" />
                            Habitación *
                          </Form.Label>
                          <Form.Select
                            className="dh-form-control"
                            value={formData.habitacion_id || ''}
                            onChange={(e) => setFormData({ ...formData, habitacion_id: parseInt(e.target.value) || null })}
                            required
                          >
                            <option value="">Seleccione habitación...</option>
                            {habitaciones.map((h) => (
                              <option key={h.id} value={h.id}>
                                {h.numero} - Piso {h.piso}
                              </option>
                            ))}
                          </Form.Select>
                          <Form.Text className="text-muted">
                            La cama pertenece a la habitación seleccionada
                          </Form.Text>
                        </Form.Group>
                      </Col>
                    </Row>
                  </Form>
                </div>
                <div className="modal-footer">
                  <Button className="btn-dh-secondary" onClick={handleCloseModal}>
                    <FaTimes className="me-2" />
                    Cancelar
                  </Button>
                  <Button 
                    className="btn-dh-primary" 
                    onClick={handleSaveCama}
                    disabled={!formData.codigo?.trim() || !formData.habitacion_id}
                  >
                    <FaCheck className="me-2" />
                    {modalMode === 'create' ? 'Crear Cama' : 'Actualizar Cama'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {showEstadoModal && camaEstadoTarget && (
          <div className="modal show d-block dh-modal-estado" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title d-flex align-items-center">
                    {esInactivacion ? (
                      <>
                        <FaBan className="me-2" />
                        Inactivar cama
                      </>
                    ) : (
                      <>
                        <FaCheck className="me-2" />
                        Activar cama
                      </>
                    )}
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={handleCerrarModalEstado}
                    disabled={procesandoEstado}
                  />
                </div>
                <div className="modal-body p-4">
                  <p className="mb-3" style={{ color: '#374151', fontSize: '1.05rem' }}>
                    Cama: <strong>{camaEstadoTarget.codigo || camaEstadoTarget.numero}</strong>
                    {' — '}
                    Hab. {camaEstadoTarget.habitacion_numero || camaEstadoTarget.numero_habitacion || 'N/A'}
                  </p>

                  {errorModalEstado && (
                    <Alert className="dh-alert dh-alert-danger mb-3">
                      <FaExclamationTriangle className="me-2" />
                      {errorModalEstado}
                    </Alert>
                  )}

                  {esInactivacion ? (
                    <>
                      {loadingValidacionEstado ? (
                        <div className="text-center py-4">
                          <div className="spinner-border text-primary mb-3" role="status">
                            <span className="visually-hidden">Validando...</span>
                          </div>
                          <p className="text-muted mb-0">
                            Verificando reservas y check-ins en curso...
                          </p>
                        </div>
                      ) : validacionInactivacion?.puede_inactivar ? (
                        <Alert className="dh-alert dh-alert-info mb-0">
                          <FaCheck className="me-2" />
                          {validacionInactivacion.mensaje}
                          <div className="mt-2 small text-muted">
                            Al inactivar, la cama no aparecerá en reservas ni en el reporte
                            de camas (con filtro de camas activas).
                          </div>
                        </Alert>
                      ) : validacionInactivacion ? (
                        <Alert className="dh-alert dh-alert-danger mb-0">
                          <FaExclamationTriangle className="me-2" />
                          {validacionInactivacion.mensaje}
                          {(validacionInactivacion.reservas > 0 ||
                            validacionInactivacion.check_ins > 0) && (
                            <ul className="mb-0 mt-2 ps-3">
                              {validacionInactivacion.reservas > 0 && (
                                <li>
                                  {validacionInactivacion.reservas} reserva
                                  {validacionInactivacion.reservas !== 1 ? "s" : ""} en curso
                                </li>
                              )}
                              {validacionInactivacion.check_ins > 0 && (
                                <li>
                                  {validacionInactivacion.check_ins} check-in
                                  {validacionInactivacion.check_ins !== 1 ? "s" : ""} en curso
                                </li>
                              )}
                            </ul>
                          )}
                        </Alert>
                      ) : null}
                    </>
                  ) : (
                    <Alert className="dh-alert dh-alert-warning mb-0">
                      <FaExclamationTriangle className="me-2" />
                      ¿Confirma que desea activar la cama{' '}
                      <strong>{camaEstadoTarget.codigo || camaEstadoTarget.numero}</strong>?
                      Volverá a estar disponible para reservas y reportes.
                    </Alert>
                  )}
                </div>
                <div className="modal-footer border-0 px-4 pb-4">
                  <Button
                    className="btn-dh-secondary"
                    onClick={handleCerrarModalEstado}
                    disabled={procesandoEstado}
                  >
                    <FaTimes className="me-2" />
                    {esInactivacion &&
                    validacionInactivacion &&
                    !validacionInactivacion.puede_inactivar
                      ? "Cerrar"
                      : "Cancelar"}
                  </Button>
                  {(!esInactivacion || validacionInactivacion?.puede_inactivar) && (
                    <Button
                      className={esInactivacion ? "btn-dh-danger" : "btn-dh-primary"}
                      onClick={handleConfirmarCambioEstado}
                      disabled={!puedeConfirmarEstado || loadingValidacionEstado}
                    >
                      {procesandoEstado ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" />
                          Procesando...
                        </>
                      ) : esInactivacion ? (
                        <>
                          <FaBan className="me-2" />
                          Confirmar inactivación
                        </>
                      ) : (
                        <>
                          <FaCheck className="me-2" />
                          Confirmar activación
                        </>
                      )}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        <ToastContainer
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
        />
      </div>
    </>
  );
};

export default MantenimientoCamas;